/**
 * Cómo se nombra la etapa de un candidato, en un solo lugar.
 *
 * Vivía copiado en la ficha del candidato y no existía en el listado de
 * entrevistas, que mostraba otra cosa: la decisión de la entrevista. Tener dos
 * vocabularios para «estatus» es lo que hacía que la misma persona se leyera
 * «Aprobado» en una pantalla y «Seleccionado» en la otra.
 *
 * Ojo con `etiquetaDeEtapa` de reclutamientoTablero, que es parecida pero
 * responde otra pregunta: ahí interesa la columna del tablero, y por eso
 * `pendiente_direccion` se llama «Aprobado por RH». Aquí interesa la etapa
 * exacta, que es lo que se dice en una ficha o en una tabla.
 */

import type { ColorDeBadge } from '@/shared/components/Badge'
import { colorDeEstatusDeCandidato } from '@/features/rh/lib/colorDeEstatusDeCandidato'

export interface EtiquetaDeEstatus {
  label: string
  color: ColorDeBadge
}

const ESTATUS: Record<string, EtiquetaDeEstatus> = {
  pre_registro: { color: colorDeEstatusDeCandidato('pre_registro'), label: 'Pre-registro' },
  registrado: { color: colorDeEstatusDeCandidato('registrado'), label: 'Registrado' },
  pendiente: { color: colorDeEstatusDeCandidato('pendiente'), label: 'Pendiente' },
  en_proceso: { color: colorDeEstatusDeCandidato('en_proceso'), label: 'En proceso' },
  entrevista_rh: { color: colorDeEstatusDeCandidato('entrevista_rh'), label: 'Entrevista RH' },
  entrevista_solicitante: { color: colorDeEstatusDeCandidato('entrevista_solicitante'), label: 'Aprobado por RH' },
  pre_seleccionado: { color: colorDeEstatusDeCandidato('pre_seleccionado'), label: 'Aprobado por RH' },
  // RH-03.1: el cierre lo decide RH, y puede pasar por Dirección.
  pendiente_cierre: { color: colorDeEstatusDeCandidato('pendiente_cierre'), label: 'Por cerrar (RH)' },
  pendiente_direccion: { color: colorDeEstatusDeCandidato('pendiente_direccion'), label: 'En validación de Dirección' },
  // SRH-311: Dirección ya decidió y RH confirma el cierre.
  decision_direccion: { color: colorDeEstatusDeCandidato('decision_direccion'), label: 'Decisión de Dirección recibida' },
  seleccionado: { color: colorDeEstatusDeCandidato('seleccionado'), label: 'Seleccionado' },
  rechazado_evaluacion_rh: { color: colorDeEstatusDeCandidato('rechazado_evaluacion_rh'), label: 'Rechazado RH' },
  contratado: { color: colorDeEstatusDeCandidato('contratado'), label: 'Contratado' },
  rechazado: { color: colorDeEstatusDeCandidato('rechazado'), label: 'Rechazado' },
}

const SIN_RECONOCER = ESTATUS.registrado

export const etiquetaDeEstatus = (status?: string | null): EtiquetaDeEstatus =>
  (status && ESTATUS[status]) || SIN_RECONOCER

/**
 * Qué decir en la columna «Estatus» del listado de entrevistas: lo que resolvió
 * la entrevista, con los mismos tres valores que el filtro de esa pantalla.
 *
 * La etapa del candidato va en su propia columna (`etiquetaDeEstatus`). Antes
 * las dos se mezclaban en una sola pastilla y la fila decía «Seleccionado»
 * bajo un filtro «Aprobado».
 */
export function decisionDeLaEntrevista(decision?: string | null): EtiquetaDeEstatus {
  if (decision === 'aprobado') return { color: 'verde', label: 'Aprobado' }
  if (decision === 'rechazado') return { color: 'rojo', label: 'Rechazado' }
  return { color: 'amarillo', label: 'Pendiente' }
}
